import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import "./book-tour.css";

/**
 * Book Underground Mine Tour — redesigned public page (route: /book-tour).
 *
 * Visual/interaction ground truth: design-ref/public/Book Tour.html
 * Functional ground truth: frontend/src/pages/BookTourPage.js — every fetch,
 * handler, state transition, validation, capacity rule, the 2.8% booking fee
 * math, the Stripe checkout redirect and all its error paths are preserved
 * exactly. Revenue-critical page.
 */

const BOOKING_FEE_PERCENT = 2.8;

const RBookTourPage = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [availableTimes, setAvailableTimes] = useState([]);
  const [timeSlots, setTimeSlots] = useState({});
  const [isDateBlocked, setIsDateBlocked] = useState(false);
  const [blackoutReason, setBlackoutReason] = useState("");
  const [tickets, setTickets] = useState({
    adult: 0,
    child: 0
  });
  const [selectedPackages, setSelectedPackages] = useState({
    family: [],
    custom: []
  });
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "",
    visitingFrom: ""
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(true);
  const [showRefundModal, setShowRefundModal] = useState(true);
  const [loading, setLoading] = useState(false);
  const [tourBookingEnabled, setTourBookingEnabled] = useState(true);
  const [pricing, setPricing] = useState({
    adult: 15.5,
    child: 8.5,
    family_packages: [],
    custom_packages: []
  });

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Underground Mine Tour Booking",
    "description": "Book your underground opal mine tour at Umoona Opal Mine, Coober Pedy. Experience authentic opal mining with professional guides.",
    "url": "https://umoonaopalmine.com.au/book-tour"
  };

  useEffect(() => {
    fetchPricing();
    fetchFeatureSettings();
  }, []);

  // Live chat widget
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://chat.turbo.net.au/widget.js";
    script.setAttribute("data-site-id", "1de29108-04e3-4a90-a55e-f61dba1f8c7d");
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const fetchFeatureSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/features/public`);
      if (response.ok) {
        const data = await response.json();
        setTourBookingEnabled(data.tour_booking_enabled !== false);
      }
    } catch (error) {
      console.error("Error fetching feature settings:", error);
      // Default to enabled on error
      setTourBookingEnabled(true);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tours/pricing`);
      if (response.ok) {
        const data = await response.json();
        setPricing(data);
      }
    } catch (error) {
      console.error("Error fetching pricing:", error);
    }
  };

  const checkAvailability = useCallback(async (date) => {
    if (!date) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tours/availability/${date}`);
      if (response.ok) {
        const data = await response.json();
        if (data.blocked) {
          setIsDateBlocked(true);
          setBlackoutReason(data.blackout_reason || "");
          setAvailableTimes([]);
          setTimeSlots({});
          setSelectedTime("");
        } else {
          setIsDateBlocked(false);
          setBlackoutReason("");
          // Use the available times from the API (it already respects the tour schedule settings)
          setAvailableTimes(data.available_times || ["10:00", "14:00", "16:00"]);
          setTimeSlots(data.time_slots || {});
          // Clear selected time if it's now sold out
          if (data.time_slots) {
            setSelectedTime((prev) => {
              const slot = data.time_slots[prev];
              if (slot && slot.total_remaining <= 0) return "";
              return prev;
            });
          }
        }
      }
    } catch (error) {
      console.error("Error checking availability:", error);
      // Default to all times on error
      setAvailableTimes(["10:00", "14:00", "16:00"]);
      setIsDateBlocked(false);
      setBlackoutReason("");
    }
  }, []);

  useEffect(() => {
    if (selectedDate) {
      checkAvailability(selectedDate);
    }
  }, [selectedDate, checkAvailability]);

  const getRemainingCapacity = () => {
    if (!selectedTime || !timeSlots[selectedTime]) return 30;
    return timeSlots[selectedTime].total_remaining;
  };

  const updateTicketQuantity = (ticketType, change) => {
    if (change > 0 && getTotalGuests() >= getRemainingCapacity()) return;
    setTickets((prev) => ({
      ...prev,
      [ticketType]: Math.max(0, prev[ticketType] + change)
    }));
  };

  const calculateSubtotal = () => {
    let subtotal = tickets.adult * pricing.adult + tickets.child * pricing.child;

    // Add family package prices
    selectedPackages.family.forEach((pkg) => {
      subtotal += pkg.price * pkg.quantity;
    });

    // Add custom package prices
    selectedPackages.custom.forEach((pkg) => {
      subtotal += pkg.price * pkg.quantity;
    });

    return subtotal;
  };

  const calculateBookingFee = () => {
    return calculateSubtotal() * (BOOKING_FEE_PERCENT / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateBookingFee();
  };

  const getTotalGuests = () => {
    let guests = tickets.adult + tickets.child;

    // Add guests from family packages using the guest_count field
    selectedPackages.family.forEach((pkg) => {
      if (pkg.guest_count) {
        guests += pkg.guest_count * pkg.quantity;
      }
    });

    return guests;
  };

  const addPackage = (type, packageData) => {
    const guestsInPackage = packageData.guest_count || parseInt(packageData.name?.match(/\d+/)?.[0]) || 1;
    if (getTotalGuests() + guestsInPackage > getRemainingCapacity()) return;
    setSelectedPackages((prev) => {
      const packages = [...prev[type]];
      const existingIndex = packages.findIndex((p) => p.name === packageData.name);

      if (existingIndex >= 0) {
        packages[existingIndex].quantity += 1;
      } else {
        packages.push({ ...packageData, quantity: 1 });
      }

      return { ...prev, [type]: packages };
    });
  };

  const removePackage = (type, packageName) => {
    setSelectedPackages((prev) => {
      const packages = [...prev[type]];
      const existingIndex = packages.findIndex((p) => p.name === packageName);

      if (existingIndex >= 0) {
        if (packages[existingIndex].quantity > 1) {
          packages[existingIndex].quantity -= 1;
        } else {
          packages.splice(existingIndex, 1);
        }
      }

      return { ...prev, [type]: packages };
    });
  };

  const getPackageQuantity = (type, packageName) => {
    const pkg = selectedPackages[type].find((p) => p.name === packageName);
    return pkg ? pkg.quantity : 0;
  };

  const formatTimeDisplay = (time) => {
    const hour = parseInt(time.split(":")[0]);
    if (hour === 10) return "10:00 AM";
    if (hour === 14) return "2:00 PM";
    if (hour === 16) return "4:00 PM";
    return time;
  };

  const formatDateDisplay = (dateStr) =>
    new Date(dateStr + "T00:00:00").toLocaleDateString("en-AU", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    });

  const handleBooking = async () => {
    if (!selectedDate || !selectedTime || getTotalGuests() === 0) {
      alert("Please select date, time, and number of tickets");
      return;
    }

    if (!customerInfo.name || !customerInfo.email) {
      alert("Please fill in your name and email address");
      return;
    }

    if (!termsAccepted) {
      alert("Please accept the Terms & Conditions to proceed with your booking");
      return;
    }

    setLoading(true);
    try {
      // Build tickets array with individual tickets
      const ticketsArray = [
        { ticket_type: "adult", quantity: tickets.adult, price_per_ticket: pricing.adult },
        { ticket_type: "child", quantity: tickets.child, price_per_ticket: pricing.child }
      ].filter((t) => t.quantity > 0);

      // Add family packages as ticket entries - map package name to ticket_type
      selectedPackages.family.forEach((pkg) => {
        // Map package name to backend ticket_type enum
        let ticketType = "adult"; // fallback
        const guestCount = pkg.guests || parseInt(pkg.name.match(/\d+/)?.[0]) || 0;

        if (guestCount === 3 || pkg.name.includes("3")) {
          ticketType = "family_3";
        } else if (guestCount === 4 || pkg.name.includes("4")) {
          ticketType = "family_4";
        } else if (guestCount === 5 || pkg.name.includes("5")) {
          ticketType = "family_5";
        } else if (guestCount === 6 || pkg.name.includes("6")) {
          ticketType = "family_6";
        } else if (guestCount === 7 || pkg.name.includes("7")) {
          ticketType = "family_7";
        } else if (guestCount === 8 || pkg.name.includes("8")) {
          ticketType = "family_8";
        } else if (pkg.ticket_type) {
          ticketType = pkg.ticket_type;
        }

        ticketsArray.push({
          ticket_type: ticketType,
          quantity: pkg.quantity || 1,
          price_per_ticket: pkg.price
        });
      });

      // Add custom packages - these need to be broken down into adult tickets
      selectedPackages.custom.forEach((pkg) => {
        // Custom packages are converted to adult tickets for simplicity
        ticketsArray.push({
          ticket_type: "adult",
          quantity: pkg.guests || 1,
          price_per_ticket: pkg.price / (pkg.guests || 1)
        });
      });

      const bookingData = {
        tour_date: selectedDate,
        tour_time: selectedTime,
        tickets: ticketsArray,
        customer: {
          name: customerInfo.name,
          email: customerInfo.email,
          phone: customerInfo.phone,
          visiting_from: customerInfo.visitingFrom
        },
        subtotal: calculateSubtotal(),
        booking_fee: calculateBookingFee(),
        booking_fee_percent: BOOKING_FEE_PERCENT,
        total_amount: calculateTotal(),
        guest_count: getTotalGuests()
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tours/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const errorData = await response.json();
        // Handle different error formats
        let errorMessage = "Booking failed";
        if (typeof errorData.detail === "string") {
          errorMessage = errorData.detail;
        } else if (errorData.detail && typeof errorData.detail === "object") {
          errorMessage = errorData.detail.message || JSON.stringify(errorData.detail);
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error creating booking:", error);
      alert("Booking failed: " + (error.message || "Unknown error occurred"));
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split("T")[0];

  const totalGuests = getTotalGuests();
  const remainingCapacity = getRemainingCapacity();
  const hasFamilyPackages = pricing.family_packages && pricing.family_packages.length > 0;
  const hasCustomPackages = pricing.custom_packages && pricing.custom_packages.length > 0;

  // Show disabled state if tour booking is not enabled
  if (!tourBookingEnabled) {
    return (
      <MarketingShell pageClass="pg-book-tour">
        <SEOHead
          title="Tour Bookings - Umoona Opal Mine"
          description="Underground mine tour information for Umoona Opal Mine, Coober Pedy"
        />
        <section className="u-sectpad u-sect-deep bt-unavail">
          <div className="bt-unavail__card">
            <div className="bt-unavail__icon" aria-hidden="true">📅</div>
            <h1 className="bt-unavail__title">Online Tour Bookings Unavailable</h1>
            <p className="bt-unavail__sub">Our online tour booking system is temporarily unavailable.</p>
            <div className="bt-unavail__notice">
              <strong>Don't worry! You can still join our underground tours.</strong>
              <p>
                Simply visit our opal shop <strong>10 minutes before tour time</strong> to purchase tickets in person
                from our friendly staff.
              </p>
              <div className="bt-unavail__block">
                <p className="bt-unavail__label">Daily Tour Times:</p>
                <p>• 10:00 AM</p>
                <p>• 2:00 PM</p>
                <p>• 4:00 PM</p>
              </div>
              <div className="bt-unavail__block">
                <p className="bt-unavail__label">Location:</p>
                <p>Umoona Opal Mine &amp; Museum</p>
                <p>14 Hutchison St, Coober Pedy SA 5723</p>
                <p>
                  <strong>Phone:</strong> (08) 8672 5288
                </p>
              </div>
            </div>
            <div className="bt-unavail__btns">
              <Link to="/" className="u-btn u-btn--primary">← Back to Home</Link>
              <Link to="/contact" className="u-btn u-btn--ghost">Get Directions</Link>
            </div>
          </div>
        </section>
      </MarketingShell>
    );
  }

  return (
    <MarketingShell pageClass="pg-book-tour">
      <SEOHead
        title="Book Underground Mine Tour - Umoona Opal Mine Tickets"
        description="Book your underground opal mine tour at Umoona Opal Mine, Coober Pedy. Experience authentic opal mining with professional guides. Adult $16, Child $9."
        keywords="book opal mine tour, underground tour tickets, Coober Pedy tour booking, mine tour reservation"
        canonical="/book-tour"
        schemaData={schemaData}
      />

      {/* Non-Refundable Acknowledgement Modal */}
      {showRefundModal && (
        <>
          <div className="bt-modal__overlay" />
          <div className="bt-modal__wrap" role="dialog" aria-modal="true" aria-label="Important Notice">
            <div className="bt-modal__card">
              <div className="bt-modal__head">
                <span className="bt-modal__headicon" aria-hidden="true">⚠️</span>
                <h2>Important Notice</h2>
              </div>
              <div className="bt-modal__body">
                <p>
                  All online ticket sales are final. Tickets are non-refundable and cannot be exchanged, transferred,
                  or rescheduled to a different date or time.
                </p>
                <p>
                  If you are unsure of travel dates or tour times, you can visit our opal shop on the day you arrive
                  to check availability.
                </p>
              </div>
              <div className="bt-modal__btns">
                <button type="button" className="bt-modal__cancel" onClick={() => navigate("/")}>
                  Cancel
                </button>
                <button type="button" className="bt-modal__ok" onClick={() => setShowRefundModal(false)}>
                  Acknowledge
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Hero */}
      <div className="u-hero">
        <img className="u-hero__img" src="/design/assets/tour-2.jpg" alt="Guided underground opal mine tour at Umoona, Coober Pedy" />
        <div className="u-hero__scrim" />
        <div className="u-hero__inner">
          <div className="u-hero__eyebrow">Reserve your underground experience</div>
          <h1 className="u-hero__title">Book Underground Mine Tour</h1>
          <p className="u-hero__sub">
            Reserve your authentic underground opal mine experience in Coober Pedy. Adults ${pricing.adult.toFixed(2)}{" "}
            · Children ${pricing.child.toFixed(2)} · Tours 10am, 2pm &amp; 4pm.
          </p>
        </div>
      </div>

      {/* Booking flow */}
      <section className="u-sectpad u-sect-deep">
        <div className="bt-notice">
          <strong>Important notice:</strong> All online ticket sales are final — non-refundable and cannot be
          exchanged, transferred or rescheduled. Unsure of dates? Visit our opal shop on the day to check
          availability. 🎁 10% off opal jewellery with online bookings (excludes watches, loose stones &amp; sale
          items).
        </div>

        <div className="u-split bt-layout">
          {/* Progressive 5-step form */}
          <div>
            {/* Step 1: Select date */}
            <div className="bt-step">
              <div className="bt-step__head">
                <span className="bt-step__num">1</span>
                <span className="bt-step__title">Select date</span>
              </div>
              <input
                type="date"
                className="bt-input"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={today}
                aria-label="Tour date"
              />
              {isDateBlocked && (
                <div className="bt-alert bt-alert--red bt-alert--below">
                  <strong>
                    Tickets unavailable for this date{blackoutReason ? `: ${blackoutReason}` : ""}
                  </strong>
                </div>
              )}
            </div>

            {/* Step 2: Select tour time */}
            {selectedDate && !isDateBlocked && (
              <div className="bt-step">
                <div className="bt-step__head">
                  <span className="bt-step__num">2</span>
                  <span className="bt-step__title">Select tour time</span>
                </div>
                <div className="bt-times">
                  {availableTimes.map((time) => {
                    const slotInfo = timeSlots[time];
                    const isSoldOut = slotInfo && slotInfo.total_remaining <= 0;
                    return (
                      <button
                        key={time}
                        type="button"
                        disabled={isSoldOut}
                        onClick={() => !isSoldOut && setSelectedTime(time)}
                        className={
                          "bt-time" +
                          (selectedTime === time ? " bt-time--active" : "") +
                          (isSoldOut ? " bt-time--soldout" : "")
                        }
                      >
                        {formatTimeDisplay(time)}
                        {isSoldOut ? " · Sold Out" : ""}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 3: Number of tickets */}
            {selectedTime && (
              <div className="bt-step">
                <div className="bt-step__head">
                  <span className="bt-step__num">3</span>
                  <span className="bt-step__title">Number of tickets</span>
                </div>

                {remainingCapacity < 30 && (
                  <div
                    className={
                      "bt-alert bt-alert--cap " + (remainingCapacity <= 5 ? "bt-alert--red" : "bt-alert--amber")
                    }
                  >
                    {remainingCapacity} spot{remainingCapacity !== 1 ? "s" : ""} remaining for this tour time
                  </div>
                )}

                {/* Adults */}
                <div className="bt-ticketrow">
                  <div>
                    <div className="bt-ticketrow__name">
                      Adults <span className="bt-price">${pricing.adult.toFixed(2)}</span>
                    </div>
                    <div className="bt-ticketrow__sub">Ages 16 and over</div>
                  </div>
                  <div className="bt-stepper">
                    <button
                      type="button"
                      className="bt-stepper__btn"
                      onClick={() => updateTicketQuantity("adult", -1)}
                      disabled={tickets.adult === 0}
                      aria-label="Remove one adult ticket"
                    >
                      −
                    </button>
                    <span className="bt-stepper__qty">{tickets.adult}</span>
                    <button
                      type="button"
                      className="bt-stepper__btn"
                      onClick={() => updateTicketQuantity("adult", 1)}
                      disabled={totalGuests >= remainingCapacity}
                      aria-label="Add one adult ticket"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Children */}
                <div className="bt-ticketrow">
                  <div>
                    <div className="bt-ticketrow__name">
                      Children <span className="bt-price">${pricing.child.toFixed(2)}</span>
                    </div>
                    <div className="bt-ticketrow__sub">Under 16 years</div>
                  </div>
                  <div className="bt-stepper">
                    <button
                      type="button"
                      className="bt-stepper__btn"
                      onClick={() => updateTicketQuantity("child", -1)}
                      disabled={tickets.child === 0}
                      aria-label="Remove one child ticket"
                    >
                      −
                    </button>
                    <span className="bt-stepper__qty">{tickets.child}</span>
                    <button
                      type="button"
                      className="bt-stepper__btn"
                      onClick={() => updateTicketQuantity("child", 1)}
                      disabled={totalGuests >= remainingCapacity}
                      aria-label="Add one child ticket"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Family packages */}
                {hasFamilyPackages && (
                  <div className="bt-pkggroup">
                    <div className="bt-pkggroup__kicker bt-pkggroup__kicker--family">Family packages</div>
                    {pricing.family_packages.map((pkg, index) => (
                      <div key={index} className="bt-ticketrow">
                        <div>
                          <div className="bt-ticketrow__name">
                            {pkg.name} <span className="bt-price">${pkg.price.toFixed(2)}</span>
                          </div>
                          {pkg.description && <div className="bt-ticketrow__sub">{pkg.description}</div>}
                        </div>
                        <div className="bt-stepper">
                          <button
                            type="button"
                            className="bt-stepper__btn"
                            onClick={() => removePackage("family", pkg.name)}
                            disabled={getPackageQuantity("family", pkg.name) === 0}
                            aria-label={`Remove one ${pkg.name}`}
                          >
                            −
                          </button>
                          <span className="bt-stepper__qty">{getPackageQuantity("family", pkg.name)}</span>
                          <button
                            type="button"
                            className="bt-stepper__btn"
                            onClick={() => addPackage("family", pkg)}
                            disabled={totalGuests >= remainingCapacity}
                            aria-label={`Add one ${pkg.name}`}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Special offers (custom packages) */}
                {hasCustomPackages && (
                  <div className="bt-pkggroup">
                    <div className="bt-pkggroup__kicker bt-pkggroup__kicker--custom">Special offers</div>
                    {pricing.custom_packages.map((pkg, index) => (
                      <div key={index} className="bt-ticketrow">
                        <div>
                          <div className="bt-ticketrow__name">
                            {pkg.name} <span className="bt-price">${pkg.price.toFixed(2)}</span>
                          </div>
                          {pkg.description && <div className="bt-ticketrow__sub">{pkg.description}</div>}
                        </div>
                        <div className="bt-stepper">
                          <button
                            type="button"
                            className="bt-stepper__btn"
                            onClick={() => removePackage("custom", pkg.name)}
                            disabled={getPackageQuantity("custom", pkg.name) === 0}
                            aria-label={`Remove one ${pkg.name}`}
                          >
                            −
                          </button>
                          <span className="bt-stepper__qty">{getPackageQuantity("custom", pkg.name)}</span>
                          <button
                            type="button"
                            className="bt-stepper__btn"
                            onClick={() => addPackage("custom", pkg)}
                            disabled={totalGuests >= remainingCapacity}
                            aria-label={`Add one ${pkg.name}`}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!hasFamilyPackages && !hasCustomPackages && (
                  <div className="bt-note">Family packages &amp; special offers shown when available.</div>
                )}
              </div>
            )}

            {/* Step 4: Your information */}
            {totalGuests > 0 && (
              <div className="bt-step">
                <div className="bt-step__head">
                  <span className="bt-step__num">4</span>
                  <span className="bt-step__title">Your information</span>
                </div>
                <label className="bt-field">
                  <span className="bt-field__label">Full name *</span>
                  <input
                    type="text"
                    className="bt-input"
                    placeholder="Full name"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo((prev) => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </label>
                <label className="bt-field">
                  <span className="bt-field__label">Email *</span>
                  <input
                    type="email"
                    className="bt-input"
                    placeholder="you@email.com"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo((prev) => ({ ...prev, email: e.target.value }))}
                    required
                  />
                </label>
                <label className="bt-field">
                  <span className="bt-field__label">Phone</span>
                  <input
                    type="tel"
                    className="bt-input"
                    placeholder="Phone (optional)"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo((prev) => ({ ...prev, phone: e.target.value }))}
                  />
                </label>
                <label className="bt-field bt-field--last">
                  <span className="bt-field__label">Visiting from</span>
                  <input
                    type="text"
                    className="bt-input"
                    placeholder="Where are you visiting from?"
                    value={customerInfo.visitingFrom}
                    onChange={(e) => setCustomerInfo((prev) => ({ ...prev, visitingFrom: e.target.value }))}
                  />
                </label>
              </div>
            )}

            {/* Step 5: Terms & conditions */}
            {totalGuests > 0 && customerInfo.name && customerInfo.email && (
              <div className="bt-step">
                <div className="bt-step__head">
                  <span className="bt-step__num">5</span>
                  <span className="bt-step__title">Terms &amp; conditions</span>
                </div>

                <div className="bt-terms">
                  <button type="button" className="bt-terms__toggle" onClick={() => setShowTerms(!showTerms)}>
                    <span>View Full Terms &amp; Conditions</span>
                    <span className={"bt-terms__caret" + (showTerms ? " bt-terms__caret--open" : "")} aria-hidden="true">
                      ▼
                    </span>
                  </button>
                  {showTerms && (
                    <div className="bt-terms__body">
                      <div>
                        <h4>Tickets &amp; Refunds</h4>
                        <p>
                          All ticket sales are final. Tickets are non-refundable and <strong>cannot</strong> be
                          exchanged, transferred, or rescheduled to a different date or time.
                        </p>
                      </div>
                      <div>
                        <h4>Tour Participation</h4>
                        <p>
                          Guests must arrive at least 10-15 minutes prior to their scheduled tour time. Late arrivals
                          may not be permitted and will not be entitled to a refund. Participants must follow all
                          safety instructions provided by tour guides. Umoona Opal Mine reserves the right to refuse
                          entry to any guest who does not comply with safety requirements or whose behaviour poses a
                          risk to themselves or others.
                        </p>
                      </div>
                      <div>
                        <h4>Tour Modifications</h4>
                        <p>
                          Umoona Opal Mine reserves the right to modify, cancel, or reschedule tours due to unforeseen
                          circumstances, safety concerns, or operational requirements. In the event of a cancellation
                          initiated by Umoona Opal Mine, guests will be offered an alternative tour time or a full
                          refund.
                        </p>
                      </div>
                      <div>
                        <h4>Liability</h4>
                        <p>
                          Guests participate in the underground tour at their own risk. Umoona Opal Mine accepts no
                          responsibility for loss, injury, or damage to personal belongings.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <label className="bt-check">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                  />
                  <span>
                    I have read, understood and accepted the{" "}
                    <button
                      type="button"
                      className="bt-check__link"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowTerms(true);
                      }}
                    >
                      Terms &amp; Conditions
                    </button>{" "}
                    (tickets &amp; refunds, tour participation, modifications and liability).{" "}
                    <span className="bt-req">*</span>
                  </span>
                </label>

                {!termsAccepted && (
                  <p className="bt-hint">Please accept the terms and conditions to proceed with your booking.</p>
                )}
              </div>
            )}
          </div>

          {/* Sticky live booking summary */}
          <aside className="bt-summary">
            <div className="bt-summary__title">Booking summary</div>
            <div className="bt-summary__rows">
              <div className="bt-summary__row">
                <span>Date</span>
                <span className="bt-summary__val">{selectedDate ? formatDateDisplay(selectedDate) : "—"}</span>
              </div>
              <div className="bt-summary__row">
                <span>Time</span>
                <span className="bt-summary__val">{selectedTime ? formatTimeDisplay(selectedTime) : "—"}</span>
              </div>
              <div className="bt-summary__row">
                <span>Guests</span>
                <span className="bt-summary__val">{totalGuests}</span>
              </div>

              {tickets.adult > 0 && (
                <div className="bt-summary__row">
                  <span>
                    {tickets.adult} Adult{tickets.adult > 1 ? "s" : ""}
                  </span>
                  <span className="bt-summary__val">${(tickets.adult * pricing.adult).toFixed(2)}</span>
                </div>
              )}
              {tickets.child > 0 && (
                <div className="bt-summary__row">
                  <span>
                    {tickets.child} Child{tickets.child > 1 ? "ren" : ""}
                  </span>
                  <span className="bt-summary__val">${(tickets.child * pricing.child).toFixed(2)}</span>
                </div>
              )}
              {selectedPackages.family.map((pkg, index) => (
                <div key={`family-${index}`} className="bt-summary__row">
                  <span>{pkg.name}</span>
                  <span className="bt-summary__val">${pkg.price.toFixed(2)}</span>
                </div>
              ))}
              {selectedPackages.custom.map((pkg, index) => (
                <div key={`custom-${index}`} className="bt-summary__row">
                  <span>{pkg.name}</span>
                  <span className="bt-summary__val">${pkg.price.toFixed(2)}</span>
                </div>
              ))}

              <div className="bt-summary__row bt-summary__row--top">
                <span>Subtotal</span>
                <span className="bt-summary__val">${calculateSubtotal().toFixed(2)}</span>
              </div>
              <div className="bt-summary__row">
                <span>Booking fee ({BOOKING_FEE_PERCENT}%)</span>
                <span className="bt-summary__val">${calculateBookingFee().toFixed(2)}</span>
              </div>
            </div>

            <div className="bt-summary__total">
              <span>Total</span>
              <span className="bt-summary__totalval">${calculateTotal().toFixed(2)}</span>
            </div>

            <button type="button" className="bt-book" onClick={handleBooking} disabled={loading || !termsAccepted}>
              {loading ? (
                <>
                  <span className="bt-book__spin" aria-hidden="true" />
                  Processing...
                </>
              ) : (
                <>Book Tour{totalGuests > 0 ? ` - $${calculateTotal().toFixed(2)}` : ""}</>
              )}
            </button>
            {totalGuests > 0 && customerInfo.name && customerInfo.email && !termsAccepted && (
              <p className="bt-hint bt-hint--center">Please accept the Terms &amp; Conditions above to proceed</p>
            )}
            <div className="bt-secure">🔒 Secure payment via Stripe</div>

            {/* Need help? */}
            <div className="bt-help">
              <div className="bt-help__title">Need Help?</div>
              <button
                type="button"
                className="bt-help__chat"
                onClick={() => {
                  if (window.TurboChat) {
                    window.TurboChat.open();
                  }
                }}
              >
                💬 Live Chat Support
              </button>
              <div className="bt-help__row">
                📞 <a href="tel:0886725288">(08) 8672 5288</a>
              </div>
              <div className="bt-help__row">📍 14 Hutchison St, Coober Pedy</div>
            </div>
          </aside>
        </div>
      </section>

      {/* Important information */}
      <section className="u-sectpad u-sect-test">
        <div className="u-eyebrow">Important information</div>
        <h2 className="u-h2">Before your tour</h2>
        <div className="u-grid3">
          <div className="bt-info">
            <div className="bt-info__kicker">WHAT TO BRING</div>
            <div className="bt-info__title">Come prepared</div>
            <p className="bt-info__text">Comfortable closed-toe shoes · camera · a sense of adventure!</p>
          </div>
          <div className="bt-info">
            <div className="bt-info__kicker bt-info__kicker--teal">TOUR DETAILS</div>
            <div className="bt-info__title">Good to know</div>
            <p className="bt-info__text">60 minutes · 22–24°C underground · 15 steep steps · photography permitted.</p>
          </div>
          <div className="bt-info">
            <div className="bt-info__kicker bt-info__kicker--cyan">ARRIVAL</div>
            <div className="bt-info__title">10 minutes early</div>
            <p className="bt-info__text">Please arrive 10 minutes before your tour time at 14 Hutchison St.</p>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
};

export default RBookTourPage;
